import type { Route } from '@tanstack/react-router';
import type { BaseAppParams } from '@agentselly/immodossier-shared';

interface ExtractedParams {
  lang: string;
  companyId: string;
  contactId: string;
  propertyId?: string;
  dossierId?: string;
  externalReference?: string;
  [key: string]: string | undefined;
}

// FIXME: Implement typing for this function
export function useAppParams(route?: Route | unknown): BaseAppParams & {
  externalReference?: string;
  propertyId?: string;
  dossierId?: string;
} {
  let params: Record<string, string | undefined> = {};
  try {
    if (
      route &&
      typeof (route as { useParams?: () => Record<string, string | undefined> })
        .useParams === 'function'
    ) {
      params =
        (
          route as { useParams: () => Record<string, string | undefined> }
        ).useParams() || {};
    }
  } catch (error) {
    console.warn('useAppParams: Error getting route params:', error);
  }

  if (!params || typeof params !== 'object') {
    params = {};
  }

  const result: ExtractedParams = {
    ...params,
    lang: params.lang || 'de',
    companyId: params.companyId || '',
    contactId: params.contactId || '',
  };

  if (result.companyId) {
    return result;
  }

  const parts = location.pathname?.split('/')?.filter(Boolean) || [];
  const langFromPath = parts[0];
  const companyIdFromPath = parts[1];

  const hasNonContactPrefix = parts.some((p) =>
    [
      'public',
      'admin',
      'partner',
      'demo',
    ].includes(p),
  );

  const third = parts[2];
  let contactIdFromPath: string | undefined = undefined;
  if (
    !hasNonContactPrefix &&
    third &&
    ![
      'public',
      'admin',
      'partner',
      'demo',
      'cockpit',
      'dossier',
      'expose',
    ].includes(third)
  ) {
    contactIdFromPath = third;
  }

  let propertyIdFromPath: string | undefined = undefined;
  let dossierIdFromPath: string | undefined = undefined;
  let externalReferenceFromPath: string | undefined = undefined;

  const KNOWN_SUBROUTES = new Set([
    'home',
    'stats',
    'statistics',
    'expose',
    'photos',
    'calendar',
    'interest',
    'activities',
    'contract',
    'documents',
    'valuation',
    'faq',
    'demo',
  ]);

  for (let i = 0; i < parts.length; i++) {
    const seg = parts[i];
    if (!seg || typeof seg !== 'string') continue;

    const nextSeg = parts[i + 1];
    const isNextValidId = Boolean(
      nextSeg && !KNOWN_SUBROUTES.has(nextSeg.toLowerCase()),
    );

    if (
      seg === 'cockpit' &&
      contactIdFromPath &&
      i + 1 < parts.length &&
      isNextValidId
    ) {
      propertyIdFromPath = parts[i + 1];
    } else if (seg.startsWith('cockpit.')) {
      propertyIdFromPath = seg.split('.')[1];
    } else if (
      seg === 'dossier' &&
      contactIdFromPath &&
      i + 1 < parts.length &&
      isNextValidId
    ) {
      dossierIdFromPath = parts[i + 1];
    } else if (seg.startsWith('dossier.')) {
      dossierIdFromPath = seg.split('.')[1];
    } else if (seg === 'partner' && i + 1 < parts.length) {
      externalReferenceFromPath = parts[i + 1];
    } else if (seg.startsWith('partner.')) {
      externalReferenceFromPath = seg.split('.')[1];
    } else if (seg === 'expose' && i + 2 < parts.length) {
      contactIdFromPath = parts[i + 1];
      propertyIdFromPath = parts[i + 2];
    }
  }

  return {
    ...result,
    lang: langFromPath || result.lang,
    companyId: companyIdFromPath || defaultCompanyId,
    contactId: result.contactId || contactIdFromPath || '',
    propertyId: result.propertyId || propertyIdFromPath,
    dossierId: result.dossierId || dossierIdFromPath,
    externalReference: result.externalReference || externalReferenceFromPath,
  };
}

// TODO: This must come from env variables on build time
const defaultCompanyId = '70927429839';
